import { ref, type Ref, type App } from "vue";
import type { AuthService, AuthState, AuthUser } from "./authService";
import type { RouteLocationNormalized } from "vue-router";
import { getConfig } from "@/services/config.ts";

export class DevTokenAdapter implements AuthService {
  private readonly isAuthenticated: Ref<boolean>;
  private readonly isLoading: Ref<boolean>;
  private readonly user: Ref<AuthUser | undefined>;
  private readonly error: Ref<Error | undefined>;
  private accessToken: string | undefined;

  constructor(_config: Record<string, never>) {
    this.isAuthenticated = ref(false);
    this.isLoading = ref(false);
    this.user = ref(undefined);
    this.error = ref(undefined);
    this.accessToken = undefined;

    this.initializeAuth();
  }

  private async initializeAuth(): Promise<void> {
    this.isLoading.value = true;

    try {
      await this.getDevAccessToken();

      this.user.value = {
        email: "dev@example.com",
        name: "Dev User",
        picture: "https://via.placeholder.com/150",
        sub: "dev|123456789",
      };
      this.isAuthenticated.value = true;
    } catch (error) {
      console.error("DevToken - Failed to initialize:", error);
      this.error.value = error as Error;
      this.isAuthenticated.value = false;
      this.user.value = undefined;
    } finally {
      this.isLoading.value = false;
    }
  }

  private async getDevAccessToken(): Promise<void> {
    const encodedCredentials = getConfig().VITE_CUSTODY_UI_TEST_CREDENTIALS;
    if (!encodedCredentials) {
      throw new Error("VITE_CUSTODY_UI_TEST_CREDENTIALS not configured");
    }

    const credentials = JSON.parse(atob(encodedCredentials));
    const devApiBaseUrl = getConfig().VITE_DEV_API_BASE_URL;

    const res = await fetch(`${devApiBaseUrl}/login/oauth/token`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: `grant_type=password&username=${credentials?.dev.customer.u}&password=${credentials?.dev.customer.p}&audience=customer-api`,
    });

    if (!res.ok) {
      throw new Error(`Dev token request failed: ${res.status}`);
    }

    const { access_token } = await res.json();
    this.accessToken = access_token;
  }

  async getAccessToken(): Promise<string | undefined> {
    return this.accessToken;
  }

  async login(_returnTo?: string): Promise<void> {
    this.isAuthenticated.value = true;
    this.isLoading.value = false;
  }

  async logout(_returnTo?: string): Promise<void> {
    this.isAuthenticated.value = false;
    this.user.value = undefined;
    this.accessToken = undefined;
  }

  getState(): AuthState {
    return {
      isAuthenticated: this.isAuthenticated,
      isLoading: this.isLoading,
      user: this.user,
      error: this.error,
    };
  }

  shouldGuardRoute(_path: string): boolean {
    return false;
  }

  async handleCallback(_url?: string): Promise<void> {
    return Promise.resolve();
  }

  async checkSession(): Promise<void> {
    return Promise.resolve();
  }

  async authGuard(_to: RouteLocationNormalized): Promise<boolean> {
    return true;
  }

  install(app: App): void {
    app.config.globalProperties.$auth = this.getState();
  }

  requiresRealAuthEndpoints(): boolean {
    return false;
  }
}
